import posthog, { type PostHog } from 'posthog-js'
import type { AuthUser } from '@/types'

export interface PostHogConfig {
  apiKey: string | null | undefined
  apiHost: string | null | undefined
  user: AuthUser | null | undefined
}

let initialized = false
let suppressed = false

/**
 * Initialize PostHog once for the lifetime of the SPA.
 *
 * Call from the Inertia entrypoint after `createInertiaApp`. No-ops when:
 *   - apiKey is missing (PostHog not configured for this environment)
 *   - user is an admin (admins are excluded from analytics, mirroring the
 *     marketing layout exclusion)
 *   - already initialized
 *
 * SPA pageviews are managed manually via `router.on('navigate')` — we set
 * `capture_pageview: false` here so PostHog doesn't double-fire.
 */
export function initPostHog({ apiKey, apiHost, user }: PostHogConfig): PostHog | null {
  if (initialized) return posthog

  if (!apiKey) return null

  if (user?.admin) {
    suppressed = true
    return null
  }

  // A previous init in this JS context may have suppressed for an admin user;
  // a non-admin init clears that flag so tracking is re-enabled.
  suppressed = false

  posthog.init(apiKey, {
    api_host: apiHost || 'https://app.posthog.com',
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
    },
  })

  initialized = true

  if (user) {
    identifyUser(user)
  }

  return posthog
}

export function identifyUser(user: AuthUser): void {
  if (!isReady()) return

  posthog.identify(user.public_id, {
    email: user.email,
    name: user.display_name,
    first_name: user.first_name,
    last_name: user.last_name,
    user_type: user.user_type,
    admin: user.admin,
    editor: user.editor,
  })
}

export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!isReady()) return

  posthog.capture(event, properties)
}

export function capturePageview(url: string): void {
  if (!isReady()) return

  posthog.capture('$pageview', { $current_url: url })
}

/** Clear identification and the suppression flag so subsequent inits can
 *  re-enable tracking (e.g. after an impersonation switch). */
export function resetPostHog(): void {
  suppressed = false

  if (!initialized) return

  posthog.reset()
}

export function isReady(): boolean {
  return initialized && !suppressed
}

export { posthog }
